// The editor's data: turns a published version into something the form can edit and back.
// Anything the form does not touch must come back exactly as it was, including key order:
// the property order of a response schema decides the order of the fields the model returns.

export const MESSAGE_ROLES = ['developer', 'system', 'user', 'assistant'];
export const TEXT_FORMATS = ['text', 'json_object', 'json_schema'];
export const REASONING_EFFORTS = ['none', 'minimal', 'low', 'medium', 'high', 'xhigh'];
export const VERBOSITIES = ['low', 'medium', 'high'];
export const SUMMARIES = ['auto', 'concise', 'detailed'];

export const FUNCTION_TEMPLATE = {
  name: '',
  description: '',
  strict: true,
  parameters: { type: 'object', properties: {}, required: [], additionalProperties: false },
};

export const SCHEMA_TEMPLATE = {
  name: 'response',
  strict: true,
  schema: { type: 'object', properties: {}, required: [], additionalProperties: false },
};

export const formatJson = (value) => JSON.stringify(value, null, 2);

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

export function draftFromVersion(version) {
  return {
    model: version.model,
    messages: version.messages.map((message) => ({
      role: message.role,
      // Text is edited as text. Parts, for example an image, are edited as JSON.
      text: typeof message.content === 'string' ? message.content : formatJson(message.content),
      isParts: typeof message.content !== 'string',
    })),
    tools: version.tools || [],
    settings: version.settings || {},
  };
}

export function emptyDraft() {
  return {
    model: '',
    messages: [{ role: 'developer', text: '', isParts: false }],
    tools: [],
    settings: { text: { format: { type: 'text' } }, store: false },
  };
}

/** Throws when a message marked as JSON does not parse. */
export function draftToBody(draft) {
  return {
    model: draft.model.trim(),
    messages: draft.messages.map((message, index) => {
      if (!message.isParts) {
        return { role: message.role, content: message.text };
      }
      try {
        return { role: message.role, content: JSON.parse(message.text) };
      } catch (error) {
        throw new Error(`Message ${index + 1} is not valid JSON: ${error.message}`);
      }
    }),
    tools: draft.tools.length > 0 ? draft.tools : null,
    settings: draft.settings,
  };
}

const comparable = (draft) => JSON.stringify({
  model: draft.model,
  messages: draft.messages,
  tools: draft.tools,
  settings: draft.settings,
});

export const isSameDraft = (first, second) => comparable(first) === comparable(second);

/**
 * Sets a nested setting without moving the other keys. `undefined` removes the key, and a
 * parent object left empty goes with it.
 */
export function setSetting(settings, path, value) {
  const [key, ...rest] = path;
  const current = isPlainObject(settings) ? settings : {};
  const next = rest.length > 0 ? setSetting(current[key], rest, value) : value;
  const isEmptyParent = rest.length > 0 && Object.keys(next).length === 0;
  if (next === undefined || isEmptyParent) {
    const { [key]: _removed, ...others } = current;
    return others;
  }
  return { ...current, [key]: next };
}

export const textFormatOf = (settings) => settings?.text?.format?.type || 'text';

/** The format object without its type, as the schema editor shows it. */
export function schemaOf(settings) {
  const { type: _type, ...schema } = settings?.text?.format || {};
  return schema;
}

export function withTextFormat(settings, type, schema) {
  const format = type === 'json_schema' ? { type, ...(schema || SCHEMA_TEMPLATE) } : { type };
  return setSetting(settings, ['text', 'format'], format);
}

/** Models with reasoning take effort and verbosity; the older ones take temperature instead. */
export const usesReasoning = (model) => /^(gpt-5|o[1-9])/i.test((model || '').trim());

const VARIABLE = /\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/g;

/** Every {{name}} in the messages, in order of first appearance. */
export function variablesIn(messages) {
  const names = new Set();
  messages.forEach((message) => {
    for (const match of message.text.matchAll(VARIABLE)) {
      names.add(match[1]);
    }
  });
  return [...names];
}

/** Models in the prompts first, then everything else OpenAI lists, each model once. */
export function modelOptions({ used, available, current }) {
  const inUse = [...new Set([...used, current].filter(Boolean))];
  const others = available.filter((model) => !inUse.includes(model));
  const toOption = (group) => (model) => ({ value: model, label: model, group });
  return [...inUse.sort().map(toOption('Used by your prompts')), ...others.map(toOption('All models'))];
}

/** Keeps the options whose label or description contains the query. */
export function filterOptions(options, query) {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return options;
  }
  return options.filter((option) => (
    `${option.label} ${option.description || ''}`.toLowerCase().includes(needle)
  ));
}

export const firstSentence = (text) => (text || '').split(/(?<=\.)\s/)[0];

export function formatLastUsed(value, now = Date.now()) {
  if (!value) {
    return '—';
  }
  const minutes = Math.floor((now - new Date(value).getTime()) / 60000);
  if (minutes < 1) {
    return 'Just now';
  }
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  if (minutes < 24 * 60) {
    return `${Math.floor(minutes / 60)} h ago`;
  }
  const days = Math.floor(minutes / (24 * 60));
  if (days < 30) {
    return days === 1 ? '1 day ago' : `${days} days ago`;
  }
  return new Date(value).toLocaleDateString();
}

const PLATFORM_NAMES = { ios: 'iOS', android: 'Android', admin: 'Admin panel' };

/** One line for a version in the Versions panel. */
export function formatVersionUsage(version, now = Date.now()) {
  if (!version.last_requested_at) {
    return 'No requests yet';
  }
  const requests = version.requests_30_days || 0;
  const count = requests === 1 ? '1 request' : `${requests.toLocaleString()} requests`;
  const apps = Object.entries(version.requests_by_platform || {})
    .filter(([, value]) => value > 0)
    .map(([platform, value]) => `${PLATFORM_NAMES[platform] || platform} ${value.toLocaleString()}`)
    .join(', ');
  return `${count} in 30 days${apps ? ` (${apps})` : ''} · last used ${formatLastUsed(version.last_requested_at, now).toLowerCase()}`;
}

export const isVariableName = (name) => /^[A-Za-z0-9_.-]+$/.test(name);

/** Parses a function from the editor. `others` are the names already taken. */
export function parseFunction(text, others) {
  let tool;
  try {
    tool = JSON.parse(text);
  } catch (error) {
    return { error: `Not valid JSON: ${error.message}` };
  }
  if (!isPlainObject(tool)) {
    return { error: 'The definition must be a JSON object.' };
  }
  if (typeof tool.name !== 'string' || !tool.name.trim()) {
    return { error: 'The function needs a name.' };
  }
  if (others.includes(tool.name)) {
    return { error: `There is already a function called ${tool.name}.` };
  }
  return { tool };
}

export function parseObject(text) {
  try {
    const value = JSON.parse(text);
    return isPlainObject(value) ? { value } : { error: 'This must be a JSON object.' };
  } catch (error) {
    return { error: `Not valid JSON: ${error.message}` };
  }
}

const JSON_TOKEN = /("(?:\\.|[^"\\\n])*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false|null)\b|([{}[\],])/g;

/** Splits text into coloured pieces. Joined back they are always the same text, valid JSON or not. */
export function tokenizeJson(text) {
  const tokens = [];
  let position = 0;
  const push = (type, value) => {
    if (value) {
      tokens.push({ type, text: value });
    }
  };
  for (const match of text.matchAll(JSON_TOKEN)) {
    push('plain', text.slice(position, match.index));
    if (match[1]) {
      push(match[2] ? 'key' : 'string', match[1]);
      push('plain', match[2]);
    } else if (match[3]) {
      push('number', match[3]);
    } else if (match[4]) {
      push('literal', match[4]);
    } else {
      push(match[5] === ',' ? 'comma' : 'bracket', match[5]);
    }
    position = match.index + match[0].length;
  }
  push('plain', text.slice(position));
  return tokens;
}
